import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import { JobLabel } from "~/components/build/Show/routes/list/-components/TreeNode/JobLabel";
import {
  StepLink,
  StepLinkHighlight,
} from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { isJobFailed } from "~/components/build/Show/lib/useFilterStore";
import { isJobRunning } from "~/components/build/Show/lib/filterConstants";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { CommandJob } from "~/components/build/Show/lib/types";
import { isJobIgnored } from "~/components/build/Show/lib/isJobIgnored";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { CommandStep, State } from "~/lib/pipeline";

export type RetryAttemptLabel = number | "latest";

interface Props {
  step: CommandStep;
  job: CommandJob;
  /** 1-based position within the retry chain, or `latest` for the current attempt. */
  attempt: RetryAttemptLabel;
  depth: number;
}

/**
 * A single retry attempt. Renders as the same job row as the latest attempt it
 * nests under (state icon, name, queue, timing), distinguished only by an
 * attempt/latest badge. Clicking opens that attempt's job in the drawer.
 *
 * Past attempts are intentionally not failure/running highlighted — they're
 * usually failed, so highlighting them all would be noise. The latest attempt
 * takes the attention-drawing highlight when retry history is expanded.
 */
export function JobRetryItem({ step, job, attempt, depth }: Props) {
  const { build } = useBuild();
  const params = useQueryParams();

  let highlight: StepLinkHighlight | undefined;
  if (attempt === "latest") {
    if (isJobFailed(job, build)) {
      highlight = "failed";
    } else if (isJobRunning(job)) {
      highlight = "running";
    }
  }

  return (
    <StepLink
      state={isJobIgnored(job) ? State.Ignored : step.state}
      type={step.type}
      to={resolveJobUrl(job.id, { open: !isJobIgnored(job) })}
      active={job.id === params.jid}
      highlight={highlight}
      depth={depth}
    >
      <JobLabel
        job={job}
        step={step}
        depth={depth}
        attempt={attempt}
        muted={attempt !== "latest"}
      />
    </StepLink>
  );
}

export const createJobRetryNode = (
  step: CommandStep,
  job: CommandJob,
  attempt: RetryAttemptLabel,
  nodeId: string = job.id,
): TreeNode => ({
  id: nodeId,
  height: STEP_ITEM_HEIGHT,
  render: ({ depth }) => <JobRetryItem step={step} job={job} attempt={attempt} depth={depth} />,
});
